import { Skeleton, Text } from '@mantine/core';
import { useState } from 'react';
import type { GetTestRunAttemptType, GetTestRunType } from '~/server/api/routers/testRun';
import DiffViewer from '../test/diffViewer/DiffViewer';
import TestRunAttemptSwitcher from './TestRunAttemptSwitcher';
import { VersionBadge } from '~/components/common/VersionBadge';
import TestRunHookDiffViews from './TestRunHookDiffViews';

const DiffViewSkeleton = () => (
  <div className="space-y-4">
    <div className="flex items-start justify-between space-x-6">
      <div className="flex flex-col space-y-1">
        <Skeleton
          height={20}
          width={120}
          radius="md"
        />
        <Skeleton
          height={16}
          width={80}
          radius="md"
        />
      </div>
      <Skeleton
        height={30}
        width={80}
        radius="md"
      />
    </div>
    <Skeleton
      height="60vh"
      radius="md"
    />
  </div>
);

export default function TestRunDiffViewTab({
  isTestRunLoading,
  testRun,
  isFullView,
}: {
  isTestRunLoading: boolean;
  testRun: GetTestRunType;
  isFullView?: boolean;
}) {
  const [selectedTestRunAttempt, setSelectedTestRunAttempt] = useState<GetTestRunAttemptType | undefined>();
  const testRunAttempts = testRun?.TestRunAttempts || [];

  return (
    <div className="mt-4 h-full">
      {isTestRunLoading ?
        <DiffViewSkeleton />
      : testRun ?
        <div className="space-y-4">
          <div className="flex items-center">
            {testRun.status !== 'SKIPPED' && testRunAttempts.length > 0 && (
              <TestRunAttemptSwitcher
                testRunAttempts={testRunAttempts}
                isFullView={isFullView}
                onAttemptChange={setSelectedTestRunAttempt}
                runStatus={testRun.status}
              />
            )}

            <div className="ml-6">
              <TestRunHookDiffViews
                testRunAttempt={selectedTestRunAttempt}
                testRun={testRun}
              />
            </div>
          </div>

          <DiffViewer
            testId={testRun.testId}
            // Compare the input and output test contents. Since the error we logged correspond to ops/steps in the input test content, we have to show that specific version.
            prevContent={{
              testContentId: selectedTestRunAttempt?.inputTestContentId || '',
              steps: selectedTestRunAttempt?.inputTestContent?.steps ?? [],
            }}
            newContent={{
              testContentId: selectedTestRunAttempt?.outputTestContentId || '',
              steps: selectedTestRunAttempt?.outputTestContent?.steps ?? [],
            }}
            prevStepsTitle={
              <>
                <Text className="font-semibold">Original Test</Text>
                <VersionBadge
                  contentId={selectedTestRunAttempt?.inputTestContentId || ''}
                  trunkContentId={testRun.test.trunkContentId || ''}
                  testId={testRun.testId}
                />
              </>
            }
            newStepsTitle={<Text className="font-semibold">AI-modified Test</Text>}
            testRunOutput={
              selectedTestRunAttempt?.executionOutput ?? {
                aiChanges: [],
                errors: [],
                logs: [],
                conditionalBlockResults: [],
                erroredAutoHealableOperations: [],
                aiActionLogs: [],
              }
            }
            canAcceptChanges={true}
            gitBranch={testRun.testSuiteRun?.gitBranch ?? undefined}
            testRunAttemptId={selectedTestRunAttempt?.id}
          />
        </div>
      : <div className="mt-4">No auto-heal changes available.</div>}
    </div>
  );
}
